import useLearningCenter from "~/hooks/use-learning-center";
import { useRootLayoutData } from "~/root";

import { getGuestLearningData } from "./guest-learning-data";
import { areAllLevelsComplete, areAllModulesComplete } from "./helpers";
import {
  learningCenterEventNames,
  trackItemCompleted,
  trackItemOpened,
  trackItemStarted,
  trackQuizAttempt,
} from "./tracking";
import type { LearningCenterData, UpdatedDataResponse } from "./types";

type LcData = NonNullable<LearningCenterData>;

/** Builder reference whose target id is at `.value.data.id` or `.data.id`. */
type BuilderRef = { value?: { data: { id: string } }; data?: { id: string } };
/** Builder content passed by the quiz consumer (parent level / goal). */
interface ParentLevelContent {
  id: string;
  modules: { module: BuilderRef }[];
}
interface ParentGoalContent {
  id: string;
  levels: { level: BuilderRef }[];
}

/**
 * Learning Center progress mutations — ported from composable
 * `useLearningCenterMutation.js`. Each op computes the next `LearningCenterData`
 * and persists it via `useLearningCenter().updateData` (registered → SCAPI
 * resource action; guest → localStorage; both optimistic). Pure data transforms
 * + Segment tracking (stubbed until LC analytics lands).
 *
 * Note vs composable: `openGoal` drops the internal `fromStartGoal` flag (the
 * reuse is now the private `openGoalData` builder), and `completeLesson` drops
 * the `updateGuestLearnCenterData` arg (guest persistence is handled inside
 * `updateData`). (SFCC-11502 / ARTICLES-11.)
 *
 * Guest base-data note (SFCC-11729): for guests, each mutation computes its
 * `next` from the FRESH localStorage blob (`getGuestLearningData()`) rather than
 * the hook's `learningCenterData` React snapshot. The module page fires several
 * structured writes on mount (the `index.tsx` goal/level/module seed + the
 * accordion's per-lesson `markOnly` complete); reading the persisted blob each
 * time makes them additive instead of last-writer-wins on a stale snapshot.
 * Registered users still base off `learningCenterData` (the optimistic overlay
 * + revalidated root loader is authoritative there).
 */
export default function useLearningCenterMutation(
  lc?: ReturnType<typeof useLearningCenter>,
) {
  // Reader/writer must share ONE instance. `useLearningCenter` is LOCAL state
  // (per-call `useState` for optimistic/guestData + its own `useFetcher` — there
  // is no Context provider), so a write made on a different instance than the one
  // a component reads from never reaches that reader (the optimistic overlay and
  // the guest cell are separate React state). A caller that already holds its own
  // instance (e.g. the module player, whose `module` memo + ModuleProgress stars
  // read `learningCenterData`) passes it in via `lc` so the lesson-complete write
  // lands on the data it renders. Other call sites pass nothing and get the
  // `fallback` — called UNCONDITIONALLY to satisfy the Rules of Hooks, then
  // preferred only when no instance was injected. (Stepper-not-updating fix.)
  const fallback = useLearningCenter();
  const {
    learningCenterData,
    getModuleByID,
    getLevelByID,
    getGoalByID,
    updateData,
    confirmUpdateData,
  } = lc ?? fallback;
  const { isRegistered } = useRootLayoutData();

  // The base each mutation builds on. Guests read the freshest persisted blob so
  // concurrent on-mount writes accumulate; registered users use the hook state.
  const baseData = (): LcData =>
    isRegistered ? learningCenterData : getGuestLearningData();

  /** Build the data with `goalID` opened, or null if it already exists. */
  const openGoalData = (goalID: string): LcData | null => {
    const base = baseData();
    if (getGoalByID(goalID, base)) return null;
    const newData = structuredClone(base);
    const openTime = new Date().toISOString();
    newData.goals.push({ id: goalID, open: openTime, levels: [] });
    if (!newData.activeGoal) newData.activeGoal = goalID;
    trackItemOpened({
      eventType: learningCenterEventNames.goalProgressStatus,
      id: goalID,
      openedTimestamp: openTime,
    });
    return newData;
  };

  const openGoal = async (goalID: string): Promise<UpdatedDataResponse> => {
    const next = openGoalData(goalID);
    if (!next) return { success: true };
    return updateData(next);
  };

  const completeLesson = (
    lessonID: string,
    nextLessonId: string,
    moduleID: string,
    opts: { awaitServer?: boolean } = {},
  ): Promise<UpdatedDataResponse> | UpdatedDataResponse | undefined => {
    if (!lessonID) return undefined;
    // Deliberate user actions ("Continue: Lesson/Quiz") persist via the
    // server-CONFIRMED writer (awaits SCAPI + revalidates root); background
    // auto-marks keep the optimistic fire-and-forget `updateData`. (SFCC-11738.)
    const persist = opts.awaitServer ? confirmUpdateData : updateData;
    const newData = structuredClone(baseData());
    const goalID = lessonID.split("--")[0];
    const levelID = lessonID.split("--").slice(0, 2).join("--");
    const currentTime = new Date().toISOString();

    // Ensure goal exists.
    let sfccGoal = getGoalByID(goalID, newData);
    if (!sfccGoal) {
      newData.goals.push({ id: goalID, open: currentTime, levels: [] });
      if (!newData.activeGoal) newData.activeGoal = goalID;
      sfccGoal = getGoalByID(goalID, newData);
    }
    if (!sfccGoal) return undefined;

    // Ensure level exists.
    if (!sfccGoal.levels.some(level => level.id === levelID)) {
      sfccGoal.levels.push({ id: levelID, start: currentTime, modules: [] });
    }

    if (nextLessonId) {
      newData.activeLessonUrl = `${window.location.pathname}#${nextLessonId}`;
    }

    const parentLevel = getLevelByID(levelID, newData);
    if (!parentLevel) {
      return { success: false, errorMsg: `Level ${levelID} does not exist` };
    }
    if (!parentLevel.modules) parentLevel.modules = [];
    if (!parentLevel.modules.some(m => m.id === moduleID)) {
      parentLevel.modules.push({ id: moduleID, start: currentTime });
    }

    const sfccModule = getModuleByID(moduleID, newData);
    if (!sfccModule) {
      return {
        success: false,
        errorMsg: `Module with ID ${moduleID} does not exist in the customer's profile`,
      };
    }
    if (!sfccModule.lessons) sfccModule.lessons = [];
    if (sfccModule.lessons.some(lesson => lesson.id === lessonID)) {
      // Already complete. Optimistic path: nothing new to write. Confirmed path:
      // still persist the full current snapshot + revalidate so a prior
      // optimistic markOnly that added this lesson reconciles to server-truth
      // (otherwise `allLessonsComplete` wouldn't flip on the awaited click).
      return opts.awaitServer ? persist(newData) : { success: true };
    }

    const completeTime = new Date().toISOString();
    sfccModule.lessons.push({ id: lessonID, complete: completeTime });

    trackItemCompleted({
      eventType: learningCenterEventNames.lessonProgressStatus,
      id: lessonID,
      completedTimestamp: completeTime,
    });
    if (nextLessonId) {
      trackItemStarted({
        eventType: learningCenterEventNames.lessonProgressStatus,
        id: nextLessonId,
        startedTimestamp: completeTime,
      });
    }

    return persist(newData);
  };

  /** Mutates `data` in place — call within a larger mutation, no persist. */
  const completeLevel = (
    levelID: string,
    data: LearningCenterData,
    completeTime: string,
  ): void => {
    const sfccLevel = getLevelByID(levelID, data);
    if (!sfccLevel) return;
    sfccLevel.complete = completeTime;
    trackItemCompleted({
      eventType: learningCenterEventNames.levelProgressStatus,
      id: levelID,
      completedTimestamp: completeTime,
    });
  };

  /** Mutates `data` in place — call within a larger mutation, no persist. */
  const completeGoal = (
    goalID: string,
    data: LearningCenterData,
    completeTime: string,
  ): void => {
    const sfccGoal = getGoalByID(goalID, data);
    if (!sfccGoal) return;
    sfccGoal.complete = completeTime;
    trackItemCompleted({
      eventType: learningCenterEventNames.goalProgressStatus,
      id: goalID,
      completedTimestamp: completeTime,
    });
  };

  const startGoal = async (goalID: string): Promise<UpdatedDataResponse> => {
    const newData = openGoalData(goalID) ?? structuredClone(baseData());
    newData.activeGoal = goalID;
    const sfccGoal = getGoalByID(goalID, newData);
    const startTime = new Date().toISOString();
    if (sfccGoal) sfccGoal.start = startTime;
    trackItemStarted({
      eventType: learningCenterEventNames.goalProgressStatus,
      id: goalID,
      startedTimestamp: startTime,
    });
    return updateData(newData);
  };

  /** Mutates `data` in place — call within a larger mutation, no persist. */
  const startLevel = (
    levelID: string,
    goalID: string,
    data: LearningCenterData,
  ): void => {
    const sfccGoal = getGoalByID(goalID, data);
    if (!sfccGoal) return;
    const startTime = new Date().toISOString();
    sfccGoal.levels.push({ id: levelID, start: startTime, modules: [] });
    trackItemStarted({
      eventType: learningCenterEventNames.levelProgressStatus,
      id: levelID,
      startedTimestamp: startTime,
    });
  };

  const setActiveGoal = async (
    goalID: string,
  ): Promise<UpdatedDataResponse> => {
    const newData = openGoalData(goalID) ?? structuredClone(baseData());
    newData.activeGoal = goalID;
    return updateData(newData);
  };

  const setActiveLesson = async (
    lessonUrl: string,
  ): Promise<UpdatedDataResponse> => {
    const newData = structuredClone(baseData());
    newData.activeLessonUrl = lessonUrl;
    return updateData(newData);
  };

  const startModule = (
    moduleID: string,
    parentLevelID: string,
    data: LearningCenterData | string,
    url: string,
  ):
    | Promise<UpdatedDataResponse>
    | UpdatedDataResponse
    | { success: true; data: LcData } => {
    const parsed = (
      typeof data === "string" ? JSON.parse(data) : data
    ) as LearningCenterData;
    const newData = structuredClone(parsed ?? baseData());
    const startTime = new Date().toISOString();
    const parentLevel = getLevelByID(parentLevelID, newData);

    if (!parentLevel) {
      return {
        success: false,
        errorMsg: `Level with id ${parentLevelID} does not exist`,
      };
    }
    if (!parentLevel.modules) parentLevel.modules = [];

    if (parentLevel.modules.some(m => m.id === moduleID)) {
      void setActiveLesson(url);
      return { success: true, data: newData };
    }

    parentLevel.modules.push({ id: moduleID, start: startTime });
    trackItemStarted({
      eventType: learningCenterEventNames.moduleProgressStatus,
      id: moduleID,
      startedTimestamp: startTime,
    });
    newData.activeLessonUrl = url;
    return updateData(newData);
  };

  const updateQuiz = async (
    quizPassed: boolean,
    quizID: string,
    moduleID: string,
    parentLevel: ParentLevelContent,
    parentGoal: ParentGoalContent,
  ) => {
    const newData = structuredClone(baseData());
    const submitTime = new Date().toISOString();
    const sfccModule = getModuleByID(moduleID, newData);
    if (!sfccModule) {
      return {
        success: false,
        errorMsg: `Module with ID ${moduleID} does not exist in the customer's profile`,
      };
    }

    if (!sfccModule.quiz) sfccModule.quiz = { id: quizID, attempts: [] };
    const sfccQuiz = sfccModule.quiz;
    if (!sfccQuiz.attempts) sfccQuiz.attempts = [];
    sfccQuiz.attempts.push({ pass: quizPassed ? 1 : 0, time: submitTime });

    if (quizPassed) {
      sfccQuiz.complete = submitTime;
      sfccModule.complete = submitTime;
    }

    trackQuizAttempt({
      quizName: quizID,
      quizResult: quizPassed ? "Pass" : "Fail",
      attemptTimestamp: submitTime,
    });
    if (quizPassed) {
      trackItemCompleted({
        eventType: learningCenterEventNames.moduleProgressStatus,
        id: moduleID,
        completedTimestamp: submitTime,
      });
    }

    const sfccLevel = getLevelByID(parentLevel.id, newData);
    const allModulesComplete = sfccLevel
      ? areAllModulesComplete(parentLevel.modules, sfccLevel.modules)
      : false;
    let allLevelsComplete = false;

    if (allModulesComplete) {
      completeLevel(parentLevel.id, newData, submitTime);
      const sfccGoal = getGoalByID(parentGoal.id, newData);
      allLevelsComplete = sfccGoal
        ? areAllLevelsComplete(parentGoal.levels, sfccGoal.levels)
        : false;
    }

    if (allLevelsComplete) {
      completeGoal(parentGoal.id, newData, submitTime);
    }

    // Passing a quiz completes the module — a deliberate, completion-bearing
    // user action. Persist via the server-CONFIRMED writer (awaits SCAPI +
    // revalidates root) so `sfccModule.complete` is durable in `serverData`.
    // With the optimistic `updateData`, the overlay cleared on settle and root
    // never revalidated (root `shouldRevalidate` returns false for this POST),
    // so `module.complete` reverted to stale serverData → `ctaType` collapsed to
    // null → the "Next Module" CTA target emptied → silent no-op. (SFCC-11738.)
    const responseData = await confirmUpdateData(newData);

    return {
      isGoalComplete: allLevelsComplete,
      isLevelComplete: allModulesComplete,
      responseData,
    };
  };

  return {
    openGoal,
    completeLesson,
    completeLevel,
    completeGoal,
    startGoal,
    startLevel,
    startModule,
    updateQuiz,
    setActiveGoal,
    setActiveLesson,
  };
}
